import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Empty, Input, Modal, Spin, Tag, theme, Typography, type InputRef } from "antd";
import { useDebounce } from "use-debounce";

import { type TraineeStatus } from "@fitness-app/data-models/entities/Trainee";

import ClientStatusBadge from "~/modules/Trainee/components/TraineeStatusBadge/TraineeStatusBadge";
import { supabase } from "~/store/initializeStore";
import { MAX_OPEN_TRAINEES } from "./traineeWorkspaceStore";

interface TraineeSearchResult {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  status: TraineeStatus;
  user: { avatarUrl: string | null; firstName: string | null; lastName: string | null } | null;
}

const MIN_QUERY_LENGTH = 2;

// Znaki, które łamią składnię filtra `or=(...)` w PostgREST albo działają jak wildcard w ilike.
const sanitizeTerm = (term: string) => term.replace(/[,()%*\\:"]/g, " ").trim();

/** Każde słowo musi pasować do imienia, nazwiska albo e-maila, więc „anna dev" znajduje Annę Dev. */
const useTraineeQuickSearch = (query: string) => {
  const words = sanitizeTerm(query).split(/\s+/).filter(Boolean);
  const enabled = words.join(" ").length >= MIN_QUERY_LENGTH;

  return useQuery({
    queryKey: ["traineeWorkspace", "search", words],
    enabled,
    keepPreviousData: true,
    queryFn: async () => {
      let request = supabase
        .from("trainee")
        .select("id, firstName, lastName, email, status, user:userId(avatarUrl, firstName, lastName)");

      for (const word of words) {
        request = request.or(`email.ilike.%${word}%,firstName.ilike.%${word}%,lastName.ilike.%${word}%`);
      }

      const { data, error } = await request
        .order("updatedAt", { ascending: false })
        .limit(20)
        .returns<TraineeSearchResult[]>();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    },
  });
};

export const getTraineeDisplayName = (trainee: {
  firstName?: string | null;
  lastName?: string | null;
  user?: { firstName?: string | null; lastName?: string | null } | null;
}) => {
  const firstName = trainee.user?.firstName || trainee.firstName || "";
  const lastName = trainee.user?.lastName ?? trainee.lastName ?? "";
  return `${firstName} ${lastName}`.trim();
};

interface OpenTraineeModalProps {
  open: boolean;
  openTraineeIds: string[];
  onClose: () => void;
  onSelect: (traineeId: string) => void;
}

export const OpenTraineeModal = ({ open, openTraineeIds, onClose, onSelect }: OpenTraineeModalProps) => {
  const { token } = theme.useToken();
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 250);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<InputRef>(null);
  const { data, isFetching, isError } = useTraineeQuickSearch(debouncedQuery);

  const hasQuery = sanitizeTerm(query).length >= MIN_QUERY_LENGTH;
  const results = useMemo(() => (hasQuery ? (data ?? []) : []), [data, hasQuery]);

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHighlighted(0);
  }, [results]);

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    // Modal przy otwarciu ustawia fokus na sobie (po efektach dzieci) - przejmujemy go
    // w następnym takcie, żeby litery wpisane od razu po kliknięciu „+" nie przepadły.
    const timeout = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timeout);
  }, [open]);

  const select = (traineeId: string) => {
    onSelect(traineeId);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!results.length) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const trainee = results[highlighted];
      if (trainee) {
        select(trainee.id);
      }
    }
  };

  const willEvict = openTraineeIds.length >= MAX_OPEN_TRAINEES;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={560}
      destroyOnHidden
      afterOpenChange={(isOpen) => isOpen && inputRef.current?.focus()}
      styles={{ content: { padding: 0, overflow: "hidden" } }}
    >
      <div className="border-0 border-b border-solid px-4 py-3" style={{ borderColor: token.colorBorderSecondary }}>
        <Input
          ref={inputRef}
          autoFocus
          size="large"
          variant="borderless"
          prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
          suffix={isFetching && hasQuery ? <Spin size="small" /> : null}
          placeholder="Szukaj klientki po imieniu, nazwisku lub e-mailu"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          className="!px-0"
        />
      </div>

      <div ref={listRef} className="max-h-[420px] overflow-y-auto p-2" role="listbox" aria-label="Wyniki wyszukiwania">
        {!hasQuery ? (
          <Typography.Text type="secondary" className="block px-3 py-6 text-center">
            Wpisz co najmniej {MIN_QUERY_LENGTH} znaki, żeby wyszukać klientkę.
          </Typography.Text>
        ) : isError ? (
          <Typography.Text type="danger" className="block px-3 py-6 text-center">
            Nie udało się wyszukać klientek. Spróbuj ponownie.
          </Typography.Text>
        ) : !results.length && !isFetching ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nie znaleziono klientki" />
        ) : (
          results.map((trainee, index) => {
            const isOpen = openTraineeIds.includes(trainee.id);
            const isHighlighted = index === highlighted;
            const name = getTraineeDisplayName(trainee);

            return (
              <div
                key={trainee.id}
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => select(trainee.id)}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2"
                style={{ background: isHighlighted ? token.colorFillSecondary : undefined }}
              >
                <Avatar size={32} icon={<UserOutlined />} src={trainee.user?.avatarUrl} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium capitalize" style={{ color: token.colorText }}>
                    {name.toLowerCase() || trainee.email}
                  </div>
                  <div className="truncate text-xs" style={{ color: token.colorTextSecondary }}>
                    {trainee.email}
                  </div>
                </div>
                {isOpen ? (
                  <Tag bordered={false} className="!m-0">
                    Otwarta
                  </Tag>
                ) : (
                  <span className="shrink-0 text-xs">
                    <ClientStatusBadge status={trainee.status} />
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      <div
        className="flex items-center justify-between gap-4 border-0 border-t border-solid px-4 py-2 text-xs"
        style={{ borderColor: token.colorBorderSecondary, color: token.colorTextTertiary }}
      >
        <span>↑ ↓ wybór · Enter otwiera · Esc zamyka</span>
        {willEvict ? <span>Otwartych kart jest {MAX_OPEN_TRAINEES}, najstarsza zostanie zamknięta.</span> : null}
      </div>
    </Modal>
  );
};
